import React, { useState } from 'react';
import { Badge } from 'react-bootstrap';
import {
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaGlobe,
  FaStore,
  FaCog,
  FaCalendarAlt,
  FaExclamationTriangle
} from 'react-icons/fa';
import './AdvancedStoreInfo.scss';

interface Platform {
  platformKey: string;
  availabilityStates: string[];
  closingReasons: string[];
  closingMinutes: number[];
  changeable: boolean;
  availabilityState: string;
  platformRestaurantId: string;
  currentSlotEndAt: string;
  globalEntityId: string;
}

interface StoreStatus {
  message: string;
  platforms: Platform[];
  total_platforms: number;
  changeable_platforms: number;
}

interface AdvancedStoreInfoProps {
  storeStatus: StoreStatus;
}

const AdvancedStoreInfo: React.FC<AdvancedStoreInfoProps> = ({ storeStatus }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatAvailabilityState = (state: string): string => {
    const stateMap: { [key: string]: string } = {
      'OPEN': 'Abierto',
      'CLOSED': 'Cerrado',
      'CLOSED_UNTIL': 'Cerrado hasta',
      'CLOSED_TODAY': 'Cerrado hoy'
    };
    return stateMap[state] || state;
  };

  const formatClosingReason = (reason: string): string => {
    const reasonMap: { [key: string]: string } = {
      'CLOSED': 'Cerrado manualmente',
      'UPDATES_IN_MENU': 'Actualizaciones en el menú',
      'HOLIDAY_SPECIAL_DAY': 'Día festivo o especial',
      'TECHNICAL_PROBLEM': 'Problema técnico',
      'REFURBISHMENT': 'Renovación',
      'TOO_BUSY_NO_DRIVERS': 'Muy ocupado / Sin repartidores',
      'BAD_WEATHER': 'Mal clima',
      'OTHER': 'Otro'
    };
    return reasonMap[reason] || reason;
  };

  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Santiago'
      });
    } catch {
      return dateString;
    }
  };

  const formatClosingMinutes = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };  const renderPlatformDetails = (platform: Platform) => (
    <div key={platform.platformKey} className="platform-details-card">
      <div className="platform-header">
        <div className="header-content">
          <div className="platform-info">
            <div className="platform-icon">
              <FaStore />
            </div>
            <div className="platform-title">
              Plataforma {platform.platformKey}
              <span className="platform-id">{platform.globalEntityId}</span>
            </div>
          </div>
          <Badge
            bg={platform.availabilityState === 'OPEN' ? 'success' :
                platform.availabilityState === 'CLOSED_UNTIL' ? 'warning' : 'danger'}
            className="status-badge"
          >
            {formatAvailabilityState(platform.availabilityState)}
          </Badge>
        </div>
      </div>
      <div className="platform-details-content">
        <div className="details-grid">
          <div className="detail-section">
            <div className="section-header">
              <div className="section-icon">
                <FaCog />
              </div>
              <h6>Información General</h6>
            </div>
            <div className="info-item">
              <strong>ID del Restaurante:</strong>
              <span className="value">{platform.platformRestaurantId}</span>
            </div>
            <div className="info-item">
              <strong>Entidad Global:</strong>
              <span className="value">{platform.globalEntityId}</span>
            </div>
            <div className="info-item">
              <strong>Puede cambiar estado:</strong>
              <Badge bg={platform.changeable ? 'success' : 'secondary'}>
                {platform.changeable ? 'Sí' : 'No'}
              </Badge>
            </div>
          </div>
          {platform.currentSlotEndAt && (
            <div className="detail-section">
              <div className="section-header">
                <div className="section-icon">
                  <FaClock />
                </div>
                <h6>Horarios</h6>
              </div>
              <div className="info-item">
                <strong>Disponible hasta:</strong>
                <span className="value">{formatDateTime(platform.currentSlotEndAt)}</span>
              </div>
            </div>
          )}
        </div>
        {platform.availabilityStates && platform.availabilityStates.length > 0 && (
          <div className="detail-section">
            <div className="section-header">
              <div className="section-icon">
                <FaCalendarAlt />
              </div>
              <h6>Estados Disponibles</h6>
            </div>
            <div className="badge-grid">
              {platform.availabilityStates.map((state: string, index: number) => (
                <div key={index} className="badge-item">
                  {formatAvailabilityState(state)}
                </div>
              ))}
            </div>
          </div>
        )}
        {platform.closingMinutes && platform.closingMinutes.length > 0 && (
          <div className="detail-section">
            <div className="section-header">
              <div className="section-icon">
                <FaClock />
              </div>
              <h6>Tiempos de Cierre</h6>
            </div>
            <div className="badge-grid">
              {platform.closingMinutes.map((minutes: number, index: number) => (
                <div key={index} className="badge-item">
                  {formatClosingMinutes(minutes)}
                </div>
              ))}
            </div>
          </div>
        )}
        {platform.closingReasons && platform.closingReasons.length > 0 && (
          <div className="detail-section">
            <div className="section-header">
              <div className="section-icon">
                <FaExclamationTriangle />
              </div>
              <h6>Motivos de Cierre Disponibles</h6>
            </div>
            <div className="reason-grid">
              {platform.closingReasons.map((reason: string, index: number) => (
                <div key={index} className="reason-item">
                  {formatClosingReason(reason)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );  return (
    <div className="advanced-store-info">
      <button
        className="mp-button secondary w-100 advanced-info-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <FaInfoCircle className="me-2" />
        Ver Información Avanzada
        {isExpanded ? (
          <FaChevronUp className="ms-auto" />
        ) : (
          <FaChevronDown className="ms-auto" />
        )}
      </button>
      {isExpanded && (
        <div className="advanced-info-content">
          <div className="info-summary">
            <div className="summary-stats">
              <div className="summary-item">
                <div className="stat-icon">
                  <FaGlobe />
                </div>
                <div className="stat-number">{storeStatus.total_platforms}</div>
                <div className="stat-label">Total Plataformas</div>
              </div>
              <div className="summary-item">
                <div className="stat-icon">
                  <FaCog />
                </div>
                <div className="stat-number">{storeStatus.changeable_platforms}</div>
                <div className="stat-label">Configurables</div>
              </div>
              <div className="summary-item">
                <div className="stat-icon">
                  <FaStore />
                </div>
                <div className="stat-number">
                  {storeStatus.platforms.filter(p => p.availabilityState === 'OPEN').length}
                </div>
                <div className="stat-label">Abiertas</div>
              </div>
            </div>
          </div>

          <div className="platforms-details">
            <h5 className="section-title">Detalles por Plataforma</h5>
            {storeStatus.platforms.map((platform: Platform) => renderPlatformDetails(platform))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedStoreInfo;
